import type { LocationQuery } from 'vue-router';
import axiosInstance from '../config/axios';
import type { APIResp, APIRespPaginated } from '../types/APIService';
import type { FileInfo, FileInfoServer } from '../types/File';

export async function getFileInfo(file_id: number): Promise<FileInfo> {
  try {
    const res = await axiosInstance.get<APIResp<FileInfoServer>>(`/files/${file_id}`);
    const info: FileInfo = { ...res.data.data, created_at: new Date(res.data.data.created_at) };
    return info;
  }
  catch {
    return { id: 0, name: '', status: 'done', created_at: new Date(), size: 0 };
  }
}

export async function downloadFile(file_id: number) {
  try {
    // Make Axios GET request to the API
    const info = await getFileInfo(file_id);
    const response = await axiosInstance.get(`/files/${file_id}/download`, {
      responseType: 'blob', // Specify the response type as 'blob' for binary data
    });

    // Create a Blob object from the binary data
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const link = document.createElement('a');
    link.download = info.name;
    link.href = window.URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  catch (error) {
    console.error('Error downloading file:', error);
  }
}

export async function uploadFile(file: File, ac: AbortController, onProgress?: (progress: number) => void) {
  const body = new FormData();
  body.append('file', file);
  try {
    const res = await axiosInstance.post<APIResp<FileInfo>>(`/files`, body, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const progressPercentage = Math.round((progressEvent.loaded * 100) / (progressEvent?.total ?? 1));
          onProgress(progressPercentage);
        }
      },
      signal: ac.signal,
    });
    res.data.data.created_at = new Date(res.data.data.created_at);
    return res.data.data;
  }
  catch {
    return null;
  }
}

export async function getAllFiles(routeQuery: LocationQuery): Promise<APIRespPaginated<FileInfo[]>['data']> {
  try {
    const res = await axiosInstance.get<APIRespPaginated<FileInfoServer[]>>(`/files`, { params: routeQuery });
    const files: FileInfo[] = res.data.data.data.map(item => ({ ...item, created_at: new Date(item.created_at) }));

    return {
      data: files,
      total_count: res.data.data.total_count,
      pages: res.data.data.pages
    };
  }
  catch {
    return {
      data: [],
      total_count: 0,
      pages: 0,
    };
  }
}

export async function deleteFile(file_id: number) {
  try {
    const res = await axiosInstance.delete(`/files/${file_id}`);
    return res.data.data;
  }
  catch {
    return null;
  }
}
